import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import ArrowDown from "@/assets/icons/ArrowDown.svg";
import BlueCheck from "@/assets/icons/BlueCheck.svg";
import { TOption } from "@/types/types";

export type SingleSelectProps = {
  data: TOption | undefined;
  name: string;
  options: TOption[];
  onChange: (selected: TOption | undefined) => void;
  width?: string;
};

const SingleSelector = ({
  name,
  data: selectedItem,
  options,
  onChange,
  width = "298px",
}: SingleSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  
  const handleChange = (item: TOption | undefined) => {
    onChange(item);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`md:w-[${width}]`}>
      <div className="relative">
        <div
          className={`${isOpen && "bg-[#17457D]/10 rounded-[8px] "} p-1 mt-2`}
        >
          <div
            className={`bg-white px-3 py-[10px] border flex justify-between ${
              isOpen ? "border-[#4066A7]" : "border-[#D0D5DD]"
            } rounded-[8px] h-[44px] cursor-pointer`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="flex gap-x-2 items-center">
              <div className="text-[#667085] select-none">
                {selectedItem?.title ? (
                  <div className="flex h-[24px] gap-x-2 items-center">
                    {selectedItem.img && (
                      <img
                        src={selectedItem.img}
                        className="w-[24px] h-[24px]"
                        alt=""
                      />
                    )}
                    <span className="text-[#101828] text-[15px] font-medium">
                      {selectedItem.title}
                    </span>
                  </div>
                ) : (
                  <p>Select..</p>
                )}
              </div>
            </div>
            <div
              className={`absolute top-3.5 right-4 transition-all duration-300 ease-in-out ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            >
              <ArrowDown />
            </div>
          </div>
        </div>

        {isOpen && (
          <Dropdown
            options={options}
            selectedItem={selectedItem}
            onChange={handleChange} // 👈 will close dropdown on selection
          />
        )}
      </div>
    </div>
  );
};

export default SingleSelector;

// ────────────────────────────────────────────────

type DropdownProps = {
  options: TOption[];
  selectedItem: TOption | undefined;
  onChange: (selected: TOption | undefined) => void;
};

const Dropdown = ({ options, selectedItem, onChange }: DropdownProps) => {
  return (
    <div className="absolute max-h-48 drop-shadow-lg overflow-y-auto p-2 mt-2 w-full border border-[#F2F4F7] rounded-[8px] bg-white z-10">
      <div className="flex flex-col">
        {options.map((option) => (
          <SelectOption
            key={option.id}
            item={option}
            selectedItem={selectedItem}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────

type SelectOptionProps = {
  item: TOption;
  selectedItem: TOption | undefined;
  onChange: (selected: TOption | undefined) => void;
};

const SelectOption = memo(
  ({ item, selectedItem, onChange }: SelectOptionProps) => {
    const isSelected = selectedItem?.id === item.id;

    const handleChange = () => {
      onChange(isSelected ? undefined : item);
    };

    return (
      <div
        onClick={handleChange}
        className="py-[10px] px-3 hover:bg-[#F9FAFB] rounded-[8px] select-none cursor-pointer flex justify-between items-center"
      >
        <div className="inline-flex gap-x-2">
          {item.img && <img src={item.img} className="w-[28px]" alt="" />}
          <span className="text-base font-medium text-[#101828]">
            {item.title}
          </span>
        </div>
        {isSelected && <BlueCheck />}
      </div>
    );
  }
);
